import { useEffect, useState, useMemo, useCallback, useRef, memo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import {
  FiFilter,
  FiGrid,
  FiX,
  FiPlus,
  FiMinus,
  FiSearch,
} from "react-icons/fi";

const sortOptions = [
  { name: "Più Popolari", href: "#", current: true },
  { name: "Votazioni Migliori", href: "#", current: false },
  { name: "Più Recenti", href: "#", current: false },
  { name: "Prezzo: Crescente", href: "#", current: false },
  { name: "Prezzo: Decrescente", href: "#", current: false },
];

const filters = [
  {
    id: "category",
    name: "Categorie",
    options: [
      { value: "animali", label: "Animali", checked: false },
      { value: "fiori", label: "Fiori", checked: false },
      { value: "lampade", label: "Lampade", checked: false },
      { value: "oggetti", label: "Oggetti", checked: false },
    ],
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Build an optimized Cloudinary URL: auto format, auto quality, resize to 400w
const getOptimizedImageUrl = (imageUrl) => {
  if (imageUrl.startsWith("http")) return imageUrl;
  return `https://res.cloudinary.com/dndm9hex2/image/upload/f_auto,q_auto,w_400/${imageUrl}`;
};

// Enhanced ProductCard with animation
const ProductCard = memo(({ product }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="group"
    >
      <Link to={"/product/" + product.productId} className="block">
        <div className="w-full overflow-hidden rounded-xl bg-gray-100 shadow-sm mb-3 relative">
          <img
            src={getOptimizedImageUrl(product.imageUrl)}
            alt={product.productName}
            className="h-[200px] w-full object-cover object-center transition-transform duration-300 group-hover:scale-105 group-hover:opacity-90"
            loading="lazy"
            decoding="async"
            width={400}
            height={200}
          />
          {product.quantityAvailable <= 5 && product.quantityAvailable > 0 && (
            <div className="absolute top-2 right-2 bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full font-medium">
              Solo {product.quantityAvailable} rimasti
            </div>
          )}
          {product.quantityAvailable === 0 && (
            <div className="absolute top-2 right-2 bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full font-medium">
              Esaurito
            </div>
          )}
        </div>
        <div className="mt-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-800 line-clamp-1">
              {product.productName}
            </h3>
            {product.productCategory && (
              <span className="text-xs bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full">
                {product.productCategory.categoryName}
              </span>
            )}
          </div>
          <p className="mt-1 text-lg font-semibold text-[#406580]">
            €{parseFloat(product.productPrice).toFixed(2)}
          </p>
        </div>
      </Link>
    </motion.div>
  );
});

// Add pagination logic
const ITEMS_PER_PAGE = 12;

export default function StoreLayout() {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchDebounceRef = useRef(null);
  const [activeSort, setActiveSort] = useState("Più Popolari");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(
          process.env.REACT_APP_BACKEND_URI + "products/",
        );
        const data = await res.json();
        setProducts(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching products:", error);
        setLoading(false);
        setError("Si è verificato un errore durante il recupero dei prodotti.");
      }
    };

    fetchProducts();
  }, []);

  // Debounce search input
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
  }, []);

  // Reset to page 1 when filters or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFilters, debouncedSearch]);

  const handleFilterChange = (sectionId, optionValue) => {
    setSelectedFilters((prev) => {
      const sectionFilters = prev[sectionId] || new Set();
      const newFilters = new Set(sectionFilters);

      if (newFilters.has(optionValue)) {
        newFilters.delete(optionValue);
      } else {
        newFilters.add(optionValue);
      }

      return { ...prev, [sectionId]: newFilters };
    });
  };

  const handleSortChange = useCallback((sortName) => {
    setActiveSort(sortName);
  }, []);

  // Filtered, searched and sorted products
  const filteredProducts = useMemo(() => {
    if (!products.length) return [];

    let result = products.filter((product) => {
      // Apply search filter
      if (debouncedSearch.trim()) {
        const searchLower = debouncedSearch.toLowerCase();
        const nameMatch = product.productName
          .toLowerCase()
          .includes(searchLower);
        const descMatch = product.productDescription
          ?.toLowerCase()
          .includes(searchLower);
        const categoryMatch = product.productCategory?.categoryName
          .toLowerCase()
          .includes(searchLower);
        if (!(nameMatch || descMatch || categoryMatch)) return false;
      }

      // Apply category filters
      if (!Object.keys(selectedFilters).length) return true;
      return Object.keys(selectedFilters).every((filterKey) => {
        const filterSet = selectedFilters[filterKey];
        if (!filterSet?.size) return true;
        if (filterKey === "category") {
          return filterSet.has(
            product.productCategory?.categoryName.toLowerCase(),
          );
        }
        return true;
      });
    });

    // Apply sort
    switch (activeSort) {
      case "Prezzo: Crescente":
        result = [...result].sort((a, b) => a.productPrice - b.productPrice);
        break;
      case "Prezzo: Decrescente":
        result = [...result].sort((a, b) => b.productPrice - a.productPrice);
        break;
      case "Più Recenti":
        result = [...result].sort(
          (a, b) => new Date(b.creationDate) - new Date(a.creationDate),
        );
        break;
      default:
        break;
    }

    return result;
  }, [products, selectedFilters, debouncedSearch, activeSort]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  // Render the product grid
  const renderProductGrid = () => {
    if (loading) {
      return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col justify-center items-center h-[400px]"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            className="h-16 w-16 border-4 border-gray-200 border-t-[#406580] rounded-full"
          />
          <p className="mt-4 text-gray-500 font-medium">
            Caricamento prodotti...
          </p>
        </motion.div>
      );
    }

    if (error) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-lg p-6 text-center mt-8"
        >
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiX className="h-6 w-6 text-red-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Si è verificato un errore
          </h3>
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-[#406580] text-white rounded-lg hover:bg-[#305570] transition-colors"
          >
            Riprova
          </button>
        </motion.div>
      );
    }

    if (filteredProducts.length === 0) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center mt-8"
        >
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiSearch className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Nessun prodotto trovato
          </h3>
          <p className="text-gray-500">
            Prova a modificare i filtri o la ricerca.
          </p>
          <button
            onClick={() => {
              setSearchTerm("");
              setDebouncedSearch("");
              setSelectedFilters({});
            }}
            className="mt-4 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancella filtri
          </button>
        </motion.div>
      );
    }

    return (
      <div>
        <p className="text-sm text-gray-500 mb-6">
          Trovati {filteredProducts.length} prodotti
          {Object.keys(selectedFilters).length > 0 &&
            " con i filtri selezionati"}
          {debouncedSearch && ` per la ricerca "${debouncedSearch}"`}
        </p>

        <div className="grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
          {paginatedProducts.map((product) => (
            <ProductCard key={product.productId} product={product} />
          ))}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-12">
            <nav
              className="isolate inline-flex -space-x-px rounded-md shadow-sm"
              aria-label="Pagination"
            >
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center rounded-l-md px-3 py-2 ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-500 hover:bg-gray-50"
                } border border-gray-300`}
              >
                <span className="sr-only">Previous</span>
                <svg
                  className="h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => {
                  // Show limited page numbers with ellipsis for better UX
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        aria-current={currentPage === page ? "page" : undefined}
                        className={`relative inline-flex items-center px-4 py-2 border ${
                          currentPage === page
                            ? "z-10 bg-[#406580] text-white focus:z-20 border-[#406580]"
                            : "bg-white text-gray-500 hover:bg-gray-50 border-gray-300"
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === 2 || page === totalPages - 1) {
                    return (
                      <span
                        key={`ellipsis-${page}`}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-gray-700"
                      >
                        ...
                      </span>
                    );
                  }
                  return null;
                },
              )}

              <button
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center rounded-r-md px-3 py-2 ${
                  currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-500 hover:bg-gray-50"
                } border border-gray-300`}
              >
                <span className="sr-only">Next</span>
                <svg
                  className="h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </nav>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-gray-50 w-full min-h-[100vh] h-full">
      <div className="w-full">
        {/* Mobile filter dialog */}
        <Dialog
          open={mobileFiltersOpen}
          onClose={setMobileFiltersOpen}
          className="relative z-40 lg:hidden"
        >
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-black/25 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
          />

          <div className="fixed inset-0 z-40 flex">
            <DialogPanel
              as={motion.div}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-white py-4 pb-12 shadow-xl"
            >
              <div className="flex items-center justify-between px-4">
                <h2 className="text-lg font-medium text-gray-900">Filtri</h2>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  type="button"
                  onClick={() => setMobileFiltersOpen(false)}
                  className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2 text-gray-400"
                >
                  <span className="sr-only">Close menu</span>
                  <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                </motion.button>
              </div>

              {/* Search bar for mobile */}
              <div className="p-4">
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Cerca prodotti..."
                    className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#406580] focus:border-transparent"
                  />
                  <FiSearch className="absolute left-3 top-3 text-gray-400" />
                </div>
              </div>

              {/* Mobile Filters */}
              <form className="mt-4 border-t border-gray-200">
                <h3 className="sr-only">Categorie</h3>
                {filters.map((section) => (
                  <Disclosure
                    key={section.id}
                    as="div"
                    className="border-t border-gray-200 px-4 py-6"
                  >
                    {({ open }) => (
                      <>
                        <h3 className="-mx-2 -my-3 flow-root">
                          <DisclosureButton className="flex w-full items-center justify-between bg-white px-2 py-3 text-gray-400 hover:text-gray-500">
                            <span className="font-medium text-gray-900">
                              {section.name}
                            </span>
                            <span className="ml-6 flex items-center">
                              {open ? (
                                <FiMinus
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              ) : (
                                <FiPlus
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              )}
                            </span>
                          </DisclosureButton>
                        </h3>
                        <AnimatePresence>
                          {open && (
                            <DisclosurePanel
                              static
                              as={motion.div}
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                              className="overflow-hidden"
                            >
                              <div className="space-y-6 pt-6">
                                {section.options.map((option, optionIdx) => (
                                  <div
                                    key={option.value}
                                    className="flex items-center"
                                  >
                                    <input
                                      id={`filter-mobile-${section.id}-${optionIdx}`}
                                      name={`${section.id}[]`}
                                      type="checkbox"
                                      checked={
                                        selectedFilters[section.id]?.has(
                                          option.value,
                                        ) || false
                                      }
                                      onChange={() =>
                                        handleFilterChange(
                                          section.id,
                                          option.value,
                                        )
                                      }
                                      className="h-4 w-4 rounded border-gray-300 text-[#406580] focus:ring-[#406580]"
                                    />
                                    <label
                                      htmlFor={`filter-mobile-${section.id}-${optionIdx}`}
                                      className="ml-3 min-w-0 flex-1 text-gray-500"
                                    >
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </DisclosurePanel>
                          )}
                        </AnimatePresence>
                      </>
                    )}
                  </Disclosure>
                ))}
              </form>
            </DialogPanel>
          </div>
        </Dialog>

        <main className="mx-auto max-w-full px-4 sm:px-6 lg:px-28 mt-8">
          {/* Page Header with Animation */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between border-b border-gray-200 pb-6 pt-24 space-y-4 sm:space-y-0"
          >
            <div>
              <motion.h1
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-4xl font-bold tracking-tight text-gray-900 inria-serif-regular"
              >
                Prodotti
              </motion.h1>

              {/* Search Bar for Desktop */}
              <motion.div
                initial={{ opacity: 0, width: "80%" }}
                animate={{ opacity: 1, width: "100%" }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="mt-4 max-w-md"
              >
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Cerca prodotti..."
                    className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#406580] focus:border-transparent transition-all"
                  />
                  <FiSearch className="absolute left-3 top-3 text-gray-400" />
                  {searchTerm && (
                    <button
                      className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                      onClick={() => {
                        setSearchTerm("");
                        setDebouncedSearch("");
                      }}
                    >
                      <FiX />
                    </button>
                  )}
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex items-center space-x-4"
            >
              {/* Sort Dropdown */}
              <Menu as="div" className="relative inline-block text-left">
                <motion.div whileTap={{ scale: 0.97 }}>
                  <MenuButton className="group inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-all">
                    <span>{activeSort}</span>
                    <ChevronDownIcon
                      aria-hidden="true"
                      className="ml-2 h-4 w-4 text-gray-400 group-hover:text-gray-500"
                    />
                  </MenuButton>
                </motion.div>

                <MenuItems
                  as={motion.div}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none"
                >
                  <div className="py-1 divide-y divide-gray-100">
                    {sortOptions.map((option) => (
                      <MenuItem key={option.name}>
                        {({ active }) => (
                          <button
                            onClick={() => handleSortChange(option.name)}
                            className={classNames(
                              option.name === activeSort
                                ? "bg-gray-50 text-[#406580] font-medium"
                                : "text-gray-700",
                              active ? "bg-gray-50" : "",
                              "w-full text-left block px-4 py-3 text-sm transition-colors",
                            )}
                          >
                            <div className="flex items-center justify-between">
                              <span>{option.name}</span>
                              {option.name === activeSort && (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 500,
                                    damping: 15,
                                  }}
                                >
                                  <svg
                                    className="w-4 h-4 text-[#406580]"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                </motion.div>
                              )}
                            </div>
                          </button>
                        )}
                      </MenuItem>
                    ))}
                  </div>
                </MenuItems>
              </Menu>

              {/* View Options */}
              <motion.button
                whileHover={{ backgroundColor: "#f3f4f6" }}
                whileTap={{ scale: 0.97 }}
                type="button"
                className="p-2 rounded-lg text-gray-500 hover:text-gray-700 border border-gray-300 bg-white"
              >
                <span className="sr-only">View grid</span>
                <FiGrid aria-hidden="true" className="h-5 w-5" />
              </motion.button>

              {/* Mobile Filters Button */}
              <motion.button
                whileHover={{ backgroundColor: "#f3f4f6" }}
                whileTap={{ scale: 0.97 }}
                type="button"
                onClick={() => setMobileFiltersOpen(true)}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-700 border border-gray-300 bg-white lg:hidden"
              >
                <span className="sr-only">Filters</span>
                <FiFilter aria-hidden="true" className="h-5 w-5" />
              </motion.button>
            </motion.div>
          </motion.div>

          <section aria-labelledby="products-heading" className="pb-24 pt-6">
            <h2 id="products-heading" className="sr-only">
              Products
            </h2>

            <div className="grid grid-cols-1 gap-x-8 gap-y-10 lg:grid-cols-4">
              {/* Filters - Desktop */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="hidden lg:block"
              >
                {/* Active Filters Summary */}
                <AnimatePresence>
                  {Object.keys(selectedFilters).length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mb-6 bg-gray-50 rounded-lg border border-gray-200 p-4"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-gray-900">
                          Filtri Attivi
                        </h3>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setSelectedFilters({})}
                          className="text-xs text-[#406580] hover:text-[#305570] font-medium"
                        >
                          Cancella tutti
                        </motion.button>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {Object.keys(selectedFilters).map((filterKey) =>
                          Array.from(selectedFilters[filterKey]).map(
                            (value) => (
                              <motion.span
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                key={`${filterKey}-${value}`}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#406580] text-white"
                              >
                                {value}
                                <button
                                  onClick={() =>
                                    handleFilterChange(filterKey, value)
                                  }
                                  className="ml-1 text-white hover:text-white/80"
                                >
                                  <svg
                                    className="w-3 h-3"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M6 18L18 6M6 6l12 12"
                                    />
                                  </svg>
                                </button>
                              </motion.span>
                            ),
                          ),
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Filter Groups */}
                <div className="sticky top-24 space-y-4">
                  <h3 className="font-medium text-lg text-gray-900 mb-4">
                    Filtri
                  </h3>

                  {filters.map((section) => (
                    <Disclosure
                      key={section.id}
                      as="div"
                      className="border-b border-gray-200 py-4"
                      defaultOpen={true}
                    >
                      {({ open }) => (
                        <>
                          <h3 className="-my-3 flow-root">
                            <DisclosureButton className="flex w-full items-center justify-between py-3 text-sm text-gray-500 hover:text-gray-700 transition-colors">
                              <span className="font-medium text-gray-900">
                                {section.name}
                              </span>
                              <motion.span
                                animate={{ rotate: open ? 180 : 0 }}
                                transition={{ duration: 0.3 }}
                                className="ml-6 flex items-center"
                              >
                                <svg
                                  className="h-5 w-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                              </motion.span>
                            </DisclosureButton>
                          </h3>

                          <AnimatePresence>
                            {open && (
                              <DisclosurePanel
                                static
                                as={motion.div}
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                              >
                                <div className="space-y-4 pt-6 pl-2">
                                  {section.options.map((option, optionIdx) => (
                                    <motion.div
                                      key={option.value}
                                      initial={{ opacity: 0, x: -10 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ delay: optionIdx * 0.05 }}
                                      className="flex items-center"
                                    >
                                      <input
                                        id={`filter-${section.id}-${optionIdx}`}
                                        name={`${section.id}[]`}
                                        type="checkbox"
                                        checked={
                                          selectedFilters[section.id]?.has(
                                            option.value,
                                          ) || false
                                        }
                                        onChange={() =>
                                          handleFilterChange(
                                            section.id,
                                            option.value,
                                          )
                                        }
                                        className="h-4 w-4 rounded border-gray-300 text-[#406580] focus:ring-[#406580] transition-all"
                                      />
                                      <label
                                        htmlFor={`filter-${section.id}-${optionIdx}`}
                                        className="ml-3 text-sm text-gray-600 cursor-pointer hover:text-gray-900 transition-colors"
                                      >
                                        {option.label}
                                      </label>
                                    </motion.div>
                                  ))}
                                </div>
                              </DisclosurePanel>
                            )}
                          </AnimatePresence>
                        </>
                      )}
                    </Disclosure>
                  ))}
                </div>
              </motion.div>

              {/* Product grid */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="lg:col-span-3"
              >
                {renderProductGrid()}
              </motion.div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
